// --- React
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import {
  paramsToGridState,
  PERSISTED_GRID_STATE_KEYS,
} from "./gridStateParams";
import {
  getStoredColumnState,
  mergeColumnState,
  filterColumnState,
  columnStateToInitialGridState,
} from "tap/utils/gridColumnPreferences";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import {
  updateGridPreferences as updateGridPreferencesApi,
  isCancelledRequest,
} from "ppt/api";
import { GRID_PREFERENCES_STATUS } from "ppt/constants";

/**
 * Column-event sources that represent a deliberate layout change by the user.
 *
 * A whitelist rather than a blacklist, because AG Grid raises the same four
 * column events for a great deal more than user edits. `ColumnEventType` also
 * includes `flex`, `gridOptionsChanged`, `rowDataUpdated`, `rowModelUpdated`,
 * `sizeColumnsToFit`, `viewportSizeFeature`, `filterChanged` and others — all of
 * which fire on their own, none of which the user asked to persist.
 *
 * Two of those made auto-save self-sustaining. Spellbook's DataGrid gives every
 * column without an explicit width `flex: 1`, so any width recalculation raises
 * `columnResized` with source `flex`; and ag-grid-react diffs its props by
 * reference and re-applies whatever changed with `force: true`, so a single
 * re-render raises column events with source `gridOptionsChanged`. Either one
 * scheduled a save, whose optimistic Redux write re-rendered the grid, which
 * re-applied the options, which raised the event again — a save every 2.5s for
 * as long as the page was open, each one snapping columns back.
 *
 * Whitelisting means a source AG Grid adds in a future version is ignored until
 * someone decides it counts, which is the safe direction to fail for something
 * that writes to the user's saved preferences.
 */
const USER_COLUMN_CHANGE_SOURCES = [
  // Dragging a column header edge, or double-clicking it to autosize.
  "uiColumnResized",
  // Dragging a column to a new position.
  "uiColumnMoved",
  "uiColumnDragged",
  // The columns tool panel: visibility checkboxes, and reordering within it.
  "toolPanelUi",
  "toolPanelDragAndDrop",
  // Pin / autosize / reset entries in the header and cell menus.
  "columnMenu",
  "contextMenu",
];

/**
 * Hook to manage AG Grid API reference and row expansion state.
 * @returns {{
 *   AgGridApiRef: React.RefObject,
 *   getSelectedRows: () => Array|undefined,
 *   isMasterRowsExpanded: boolean,
 *   toggleMasterRows: () => void,
 *   collapseMasterRows: () => void,
 *   expandMasterRows: () => void
 * }}
 */
export const useAgGridApi = () => {
  const AgGridApiRef = useRef();
  const [isMasterRowsExpanded, setIsExpanded] = useState(false);

  const getSelectedRows = useCallback(() => {
    if (!AgGridApiRef.current?.api?.getSelectedRows) return;
    return AgGridApiRef.current.api.getSelectedRows();
  }, []);

  const _toggleRows = (toggleTo) => () => {
    try {
      if (AgGridApiRef.current != null) {
        const { api } = AgGridApiRef.current;

        if (!api.forEachNode || !api.setRowNodeExpanded || !api.refreshCell)
          return;

        api.forEachNode((node) => {
          node.setExpanded(toggleTo);
          api.setRowNodeExpanded(node, toggleTo);
        });
        api.refreshCells({ force: true });
      }
    } finally {
      setIsExpanded(toggleTo);
    }
  };

  const toggleMasterRows = useCallback(() => {
    _toggleRows(!isMasterRowsExpanded)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterRowsExpanded]);

  const collapseMasterRows = useCallback(() => _toggleRows(false)(), []);
  const expandMasterRows = useCallback(() => _toggleRows(true)(), []);

  return {
    AgGridApiRef,
    getSelectedRows,
    isMasterRowsExpanded,
    toggleMasterRows,
    collapseMasterRows,
    expandMasterRows,
  };
};

/**
 * Wire a value held outside the grid (a Redux filter, a toggle) into AG Grid's
 * external filter, and re-run the predicate whenever that value changes.
 *
 * AG Grid caches filter results, so mutating the filter value alone won't move
 * any rows — it needs an explicit `onFilterChanged()` nudge. Owning that
 * bookkeeping here keeps every caller down to a predicate.
 *
 * @param {React.RefObject} gridRef - Ref passed as agGrid.ref
 * @param {*} filterValue - Active filter; null/undefined means "no filter"
 * @param {(rowData: Object) => boolean} predicate - True when a row passes
 * @returns {{ isExternalFilterPresent: () => boolean, doesExternalFilterPass: (node: Object) => boolean }}
 *   Spread onto the agGrid prop.
 */
export const useAgGridExternalFilter = (gridRef, filterValue, predicate) => {
  const isExternalFilterPresent = useCallback(
    () => filterValue !== null && filterValue !== undefined,
    [filterValue],
  );

  const doesExternalFilterPass = useCallback(
    (node) => predicate(node.data),
    [predicate],
  );

  useEffect(() => {
    const api = gridRef?.current?.api;
    if (typeof api?.onFilterChanged === "function") {
      api.onFilterChanged();
    }
  }, [gridRef, filterValue]);

  return { isExternalFilterPresent, doesExternalFilterPass };
};

/**
 * The stateUpdated sources that can change something we persist.
 *
 * Derived from PERSISTED_GRID_STATE_KEYS: AG Grid names the source after the
 * state key that changed, so persisting only `sort` and `filter` means only
 * those two sources are worth a URL write. Everything else AG Grid emits
 * (`scroll`, `focusedCell`, `sideBar`, `columnSizing`, `columnOrder`,
 * `rowSelection`, …) is either volatile or deliberately not persisted.
 */
const PERSISTABLE_SOURCES = PERSISTED_GRID_STATE_KEYS;

/**
 * True when a stateUpdated event carries a change worth persisting.
 *
 * AG Grid dispatches `gridInitializing` across four separate startup phases and
 * unions the sources of everything landing inside a 0ms debounce, so a real
 * user change can arrive as `['gridInitializing', 'sort']`. Testing for the init
 * marker alone would silently drop it — hence checking for a persistable source
 * rather than the absence of the marker.
 *
 * @param {Array<string>} sources - `sources` from the stateUpdated event
 * @returns {boolean}
 */
export const hasPersistableStateChange = (sources = []) =>
  sources.some((source) => PERSISTABLE_SOURCES.includes(source));

/**
 * Persists AG Grid's native sort and filter state in the URL query string.
 *
 * The URL is the source of truth at mount: params are read once into a stable
 * snapshot to hand to AG Grid's `initialState`, which is an immutable initial
 * property (AG Grid ignores later changes and warns about them on every render,
 * so the reference must not change). After that, grid interaction writes back
 * out through `onWriteState`.
 *
 * @param {string} search - location.search to read the initial state from
 * @param {{sort: string, filter: string}} paramNames - Query params holding the
 *   encoded sort and filter
 * @param {(state: Object) => void} onWriteState - Called with the full GridState
 *   whenever the grid reports a persistable change
 * @returns {{ initialState: Object|undefined, onStateUpdated: (event: Object) => void }}
 *   Spread `initialState` as a top-level DataGrid prop and `onStateUpdated`
 *   onto the `agGrid` prop.
 */
export const useAgGridUrlState = (search, paramNames, onWriteState) => {
  // Snapshotted once so the reference is stable for the life of the grid. The
  // param names are read from the first render too, which is fine — they're
  // constants at every call site.
  const [initialState] = useState(() => paramsToGridState(search, paramNames));

  const onStateUpdated = useCallback(
    (event) => {
      if (!hasPersistableStateChange(event?.sources)) return;
      // The whole state goes out; narrowing to the persisted keys is the URL
      // layer's job, so callers can read sort/filter off one object.
      onWriteState(event.state);
    },
    [onWriteState],
  );

  return { initialState, onStateUpdated };
};

/**
 * Hook to manage persistent column state (width, visibility, order) in backend database with auto-save.
 * Changes are automatically persisted after 2.5 seconds of inactivity.
 *
 * Reads the preferences the session already fetched (see PPMPreferencesGate) —
 * it never fetches. Mount the grid only once `gridPreferencesStatus` is LOADED so
 * there is a layout to restore.
 *
 * Call this once per mounted grid, never once per candidate layout. Two
 * instances sharing one `gridRef` would both listen to that grid and both read
 * its column state, so each would persist the visible grid's layout under its
 * own key — silently overwriting the other's saved columns.
 *
 * @param {string} storageKey - Key from COLUMN_STATE_STORAGE_KEYS. May change
 *   over the hook's life (a grid that swaps which layout it represents); the
 *   saved layout for the new key is restored when it does.
 * @param {React.RefObject} gridRef - AG Grid ref from useAgGridApi
 * @param {Array} columnDefs - Current column definitions
 * @returns {{ initialGridState: Object|null, isLayoutRestored: boolean, resetColumnState: () => Promise<void>, hasSavedColumnState: boolean, isGridReady: boolean }}
 *   Merge `initialGridState` into the DataGrid's `initialState` prop — that is
 *   what gets the saved order, visibility and pinning onto the screen without a
 *   flicker of the default ones. Widths can't go that way and land a paint later,
 *   so hide the grid while `isLayoutRestored` is false. Skipping either still
 *   works; the user just sees the default layout first.
 */
export function useColumnStatePreferences(storageKey, gridRef, columnDefs) {
  const { gridPreferences, gridPreferencesStatus } = usePPTState();
  const { updateGridPreferences, revertGridPreferences } = usePPTActions();

  // Snapshotted the first time preferences are actually available, not on the
  // first render. The fetch is async and session-wide, so a grid that mounts
  // before it settles would otherwise freeze an empty layout here and never
  // restore anything. Callers gate the grid behind GRID_PREFERENCES_STATUS.LOADED
  // (see the PPM pages), which makes this the belt to that braces — a grid that
  // slips through still restores, one render later.
  //
  // Keyed by storageKey so a grid that switches layouts re-snapshots, and so the
  // re-snapshot picks up anything auto-saved in the meantime. Deliberately not
  // recomputed on every gridPreferences change: our own auto-save writes to it,
  // and re-applying mid-drag would fight the user.
  //
  // A ref rather than state because the value is derived and must be readable in
  // the same render it becomes available; the redux status change that makes it
  // available is what re-renders us.
  const snapshotRef = useRef(null);
  if (
    gridPreferencesStatus === GRID_PREFERENCES_STATUS.LOADED &&
    snapshotRef.current?.storageKey !== storageKey
  ) {
    snapshotRef.current = {
      storageKey,
      state: mergeColumnState(
        getStoredColumnState(gridPreferences, storageKey),
        columnDefs,
      ),
    };
  }
  const initialColumnState = snapshotRef.current?.state ?? null;

  // The same layout in the shape AG Grid can apply *before* the first paint.
  // Callers pass it as the grid's `initialState`; the effect further down stays
  // as the post-mount path for widths and for a grid that mounted ahead of the
  // preferences. Memoized because AG Grid reads `initialState` once, as the grid
  // is built, and ignores it from then on — a new object per render would be
  // wasted work. This changes only when the snapshot does, which is when the grid
  // is being given a different layout anyway.
  const initialGridState = useMemo(
    () => columnStateToInitialGridState(initialColumnState, columnDefs),
    [initialColumnState, columnDefs],
  );

  const [api, setApi] = useState(null);

  // Track the AgGridReact instance currently on the ref, not merely whether one
  // exists. A DataGrid that remounts — anything driven by a React `key`, such as
  // flipping which lens a grid shows — destroys its api and hangs a brand new
  // instance off the same ref object while this hook stays mounted. Noticing the
  // swap is what lets the restore and the listeners below re-attach to the live
  // grid instead of staying bound to a corpse.
  //
  // No dep array: React populates the ref during the commit that mounts the
  // grid, so every commit is precisely when it could have changed. The identity
  // check means a commit that sees no change bails out of re-rendering.
  const [gridInstance, setGridInstance] = useState(null);
  useEffect(() => {
    const current = gridRef?.current ?? null;
    setGridInstance((previous) => (previous === current ? previous : current));
  });

  // AG Grid hands its api over from inside ctrlsService.whenReady, which runs
  // after the commit that mounted the grid — so there is nothing to read at
  // mount, and watching commits alone would never see it arrive.
  // registerApiListener (declared public on AgGridReact) is the event for
  // exactly that moment. It doesn't replay an api that landed before we
  // subscribed, so the current one is read directly as well; that read is also
  // what lets a plain `{ api }` test double work without the listener.
  useEffect(() => {
    setApi(gridInstance?.api ?? null);
    if (!gridInstance) return;

    let active = true;
    gridInstance.registerApiListener?.((nextApi) => {
      if (active) setApi(nextApi);
    });
    // AgGridReact drops all of its listeners when it unmounts, so there is
    // nothing to detach. The flag covers the one case it can't: StrictMode
    // replays this effect against a live instance, stranding the old closure.
    return () => {
      active = false;
    };
  }, [gridInstance]);

  // The save currently in flight, if any. A ref so a newer save can abandon an
  // older one without either becoming a dependency of the other.
  const saveRequestRef = useRef(null);

  // Debounced auto-save function - persists to backend after 2.5s of inactivity.
  //
  // useMemo rather than useCallback: the debounced wrapper is the value being
  // memoized, and useCallback would rebuild it on every render only to throw it
  // away. Identity still changes on exactly these deps, so the listener effect
  // below still cancels the outgoing wrapper as it swaps.
  const debouncedAutoSave = useMemo(
    () =>
      debounce(async () => {
        // Read through the ref rather than the tracked instance: this fires up to
        // 2.5s after the event that scheduled it, and the ref is always the live
        // grid even if a swap has not been observed yet.
        const gridApi = gridRef?.current?.api;
        if (!gridApi?.getColumnState || gridApi.isDestroyed?.()) return;

        const state = gridApi.getColumnState();
        const filtered = filterColumnState(state);
        if (!filtered || filtered.length === 0) return;

        // Abandon anything still in flight. The debounce holds rapid changes to
        // one request per idle window, but a slow request can still be overtaken
        // by the next one, and the server keeps whichever arrives last — which
        // would be the older layout.
        saveRequestRef.current?.abort();
        const controller = new AbortController();
        saveRequestRef.current = controller;

        try {
          const updates = {
            [storageKey]: filtered,
          };
          // Optimistic: the reducer merges this into the existing preferences, so
          // only this grid's key is sent. Reading gridPreferences here instead
          // would put it in the dep array below, and every save changes it —
          // rebuilding the debounced function mid-flight and cancelling the
          // pending save it was created to make.
          updateGridPreferences(updates);

          await updateGridPreferencesApi(storageKey, filtered, {
            signal: controller.signal,
          });
        } catch (error) {
          // A cancelled save was superseded by a newer one, which now owns the
          // outcome — not something the user or the log needs to hear about.
          if (!isCancelledRequest(error)) {
            console.error("Failed to auto-save grid preferences:", error);
            // Don't revert on auto-save failure - keep user's view consistent
          }
        } finally {
          // Don't disown a controller a newer write has already put in its place;
          // only the request still holding the slot may clear it.
          if (saveRequestRef.current === controller) {
            saveRequestRef.current = null;
          }
        }
      }, 2500), // 2.5 second debounce
    [gridRef, storageKey, updateGridPreferences],
  );

  // Reset to defaults.
  //
  // Everything the user can see happens before this function ever yields: the
  // store is cleared and the columns are back to their definitions by the time it
  // returns, with the write to the server left running in the background. The
  // promise exists only so a caller can report a failure — awaiting it doesn't
  // hold up the reset, and nothing should gate the control on it.
  const resetColumnState = useCallback(async () => {
    const gridApi = gridRef?.current?.api;
    if (!gridApi || gridApi.isDestroyed?.()) return;

    // A reset is a write to the same key as an auto-save, so it takes part in the
    // same "latest wins" ordering. Both halves matter: a save still waiting out
    // its debounce would otherwise fire afterwards and persist the columns as
    // they were, and a save already in flight would land second and resurrect the
    // layout the user just cleared.
    debouncedAutoSave.cancel();
    saveRequestRef.current?.abort();
    const controller = new AbortController();
    saveRequestRef.current = controller;

    const previousState = gridPreferences || {};
    const previousSnapshot = snapshotRef.current;
    // Only this grid's key is cleared; the reducer merges the rest through.
    const updates = { [storageKey]: null };

    // Forget the layout we restored from, not merely the stored copy of it. The
    // snapshot is otherwise recomputed only when storageKey changes, so a grid
    // that remounts under the same key would restore the very layout the user
    // just cleared.
    snapshotRef.current = { storageKey, state: null };
    updateGridPreferences(updates);
    // AG Grid keeps column state inside the grid rather than in props, so putting
    // the columns back to their definitions is an api call, not a re-render. It
    // reports itself as source "api", which the auto-save listener ignores —
    // otherwise clearing the layout would immediately save the defaults back.
    gridApi.resetColumnState();

    try {
      await updateGridPreferencesApi(storageKey, null, {
        signal: controller.signal,
      });
    } catch (error) {
      // Superseded by a later write, which now owns both the outcome and any
      // reporting — rolling back or rethrowing here would fight it.
      if (isCancelledRequest(error)) return;
      console.error("Failed to reset grid preferences:", error);
      // The server still holds the old layout, so put the store and the snapshot
      // back in step with it. The grid is deliberately left showing the defaults:
      // the restore has already run for this grid, so nothing re-applies behind
      // the user's back, and the caller's error message is what explains why the
      // old layout will be back next visit.
      snapshotRef.current = previousSnapshot;
      revertGridPreferences(previousState);
      throw error;
    } finally {
      if (saveRequestRef.current === controller) {
        saveRequestRef.current = null;
      }
    }
  }, [
    gridRef,
    storageKey,
    gridPreferences,
    updateGridPreferences,
    revertGridPreferences,
    debouncedAutoSave,
  ]);

  // Restore saved state once per (grid instance, layout) pair. Both halves of
  // that key matter: a remounted grid is a fresh grid that needs the layout
  // applied again, and a grid that switches which layout it represents needs the
  // new one applied to the grid it already has.
  const restoredRef = useRef(null);
  // The same fact as restoredRef, in a form a render can read — see
  // isLayoutRestored below. Kept alongside the ref rather than replacing it: the
  // ref is what the effect guards on, and an effect that read its own output back
  // through the dep array would re-run itself.
  const [restoredFor, setRestoredFor] = useState(null);
  useEffect(() => {
    if (!api?.applyColumnState || api.isDestroyed?.()) return;
    // Nothing to restore yet — deliberately before the bookkeeping below, so a
    // layout that arrives later still gets applied.
    if (!initialColumnState?.length) return;

    const restored = restoredRef.current;
    if (restored?.api === api && restored?.storageKey === storageKey) return;
    // Recorded before the attempt, not after: a state that throws will throw
    // every time, and retrying would spin.
    restoredRef.current = { api, storageKey };

    // mergeColumnState has already dropped columns that no longer exist, and AG
    // Grid ignores state for colIds it doesn't recognize — this catch is the
    // last line of defence so a saved layout from an older schema can never
    // stop the grid from mounting.
    try {
      api.applyColumnState({
        state: initialColumnState,
        applyOrder: true,
      });
    } catch (error) {
      console.error("Failed to restore saved column layout:", error);
    }
    // Published whatever the outcome. A layout that throws is never going to
    // apply, and a caller hiding the grid until this lands would hide it for
    // good — a grid at its default widths beats no grid at all.
    setRestoredFor({ api, storageKey });
  }, [api, storageKey, initialColumnState]);

  // Track column changes and trigger auto-save. Re-runs when the grid instance
  // is replaced, which is what keeps auto-save alive across a remount — bound to
  // the old api, these listeners would never fire again.
  useEffect(() => {
    if (!api?.addEventListener) return;

    const onColumnEvent = (event) => {
      // Only a change the user actually made is worth persisting. See
      // USER_COLUMN_CHANGE_SOURCES — most sources fire without any user input,
      // and saving on those turns auto-save into a feedback loop.
      if (!USER_COLUMN_CHANGE_SOURCES.includes(event.source)) return;
      debouncedAutoSave();
    };

    const events = [
      "columnResized",
      "columnVisible",
      "columnPinned",
      "columnMoved",
    ];

    events.forEach((eventName) => {
      api.addEventListener(eventName, onColumnEvent);
    });

    return () => {
      // A replaced grid has already been destroyed and dropped its listeners;
      // AG Grid warns if you reach into one, so only detach from a live grid.
      if (!api.isDestroyed?.()) {
        events.forEach((eventName) => {
          api.removeEventListener(eventName, onColumnEvent);
        });
      }
      // Drop any save still *pending* for the grid/layout we're leaving, so it
      // can't read the incoming grid's columns and write them under the outgoing
      // key. A request already sent is deliberately left to land: it carries the
      // layout the user actually left behind, under the right key.
      debouncedAutoSave.cancel();
    };
  }, [api, debouncedAutoSave]);

  // Whether what is painted is the layout the user saved, rather than the
  // defaults it is about to be resized away from.
  //
  // `initialGridState` gets order, visibility and pinning onto the very first
  // paint, but widths cannot go that route: AG Grid has no pre-paint way to set
  // one column's width without also clearing flex from every column it wasn't
  // told about, and Spellbook's DataGrid gives most columns their width via
  // flex. So widths arrive through the applyColumnState above, which cannot run
  // until AG Grid has mounted and handed its api over — one paint too late.
  //
  // Callers keep the grid out of sight until this turns true instead of letting
  // that paint through. True when there is nothing saved, so a user who has
  // never customized a grid is never made to wait on a restore that isn't
  // coming.
  const isLayoutRestored =
    !initialColumnState?.length ||
    (restoredFor?.api === api && restoredFor?.storageKey === storageKey);

  // Whether this grid has a stored layout at all — the question a "Reset
  // Columns" control needs answered, since resetting to defaults is a no-op for
  // a user who is already on them.
  //
  // Read live from the store rather than from the snapshot above, which is
  // deliberately frozen at restore time. A control gating on the snapshot would
  // start out right and then go stale in both directions: still disabled after
  // the user's first drag auto-saved a layout, and still enabled after a reset
  // cleared it.
  const hasSavedColumnState = Boolean(
    getStoredColumnState(gridPreferences, storageKey),
  );

  return {
    // The saved layout as a partial GridState, to be merged into the grid's
    // `initialState` prop. Null when there is nothing saved, so a caller with no
    // other initial state can pass it straight through.
    initialGridState,
    isLayoutRestored,
    resetColumnState,
    hasSavedColumnState,
    // Whether there is a grid to act on at all. Controls that reset the layout
    // gate on this: pages hold their grid behind a loading skeleton until the
    // session's preferences settle, and a toolbar button outside that skeleton is
    // clickable for the whole wait — with no grid mounted, a reset would do
    // nothing and say nothing. Covers the shorter gap too, between the grid
    // mounting and AG Grid handing its api over.
    isGridReady: Boolean(api),
  };
}
